import dayjs from "dayjs";
import {
	Briefcase,
	Calendar,
	CheckCircle2,
	CircleDollarSign,
	Crown,
	MapPin,
	MoreVertical,
	Pencil,
	Plus,
	RefreshCw,
	Sparkles,
	Trash2,
	X,
} from "lucide-react";
import React from "react";
import { toast } from "sonner";

import { Button } from "@/app/components/ui/button";

import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { ContentSection, FilterBar } from "./shared";

const READ_ONLY_MESSAGE = "This dashboard currently supports browsing and filtering jobs; edit actions are read-only.";

const JOB_TYPE_OPTIONS = ["Full-time", "Part-time", "Contract", "Temporary", "Internship", "Other"];
const JOB_LEVEL_OPTIONS = ["Junior", "Mid-Level", "Senior"];
const WORK_ENVIRONMENT_OPTIONS = ["In-office", "Hybrid", "Remote"];

function Modal({ title, children, onClose, width = "max-w-xl" }) {
	return (
		<div className="modal-backdrop" onClick={onClose}>
			<div className={`modal-content ${width}`} onClick={(event) => event.stopPropagation()}>
				<div className="mb-4 flex items-center justify-between">
					<h2 className="text-lg font-bold text-foreground">{title}</h2>
					<button type="button" onClick={onClose} className="rounded-lg p-1.5 hover:bg-secondary">
						<X className="h-4 w-4" />
					</button>
				</div>
				{children}
			</div>
		</div>
	);
}

function JobsLoadingState() {
	return (
		<div className="rounded-lg border border-border bg-white py-12 text-center">
			<RefreshCw className="mx-auto mb-3 h-6 w-6 animate-spin text-primary-700" />
			<h3 className="mb-1 font-semibold text-foreground">Loading jobs</h3>
			<p className="text-sm text-muted-foreground">Fetching the latest company roles from the backend.</p>
		</div>
	);
}

function JobsErrorState({ onRetry }) {
	return (
		<div className="rounded-lg border border-red-100 bg-white py-10 text-center">
			<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-red-50">
				<Briefcase className="h-6 w-6 text-red-600" />
			</div>
			<h3 className="mb-1 font-semibold text-foreground">Could not load jobs</h3>
			<p className="mb-4 text-sm text-muted-foreground">Failed to load jobs from the backend. Try again.</p>
			<Button onClick={onRetry}>
				<RefreshCw className="mr-2 h-4 w-4" />
				Retry
			</Button>
		</div>
	);
}

function JobsEmptyState({ icon, title, description, action }) {
	return (
		<div className="rounded-lg border-2 border-dashed border-border bg-white py-8 text-center">
			<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary">
				{icon}
			</div>
			<h3 className="mb-1 font-semibold text-foreground">{title}</h3>
			<p className="mb-3 text-sm text-muted-foreground">{description}</p>
			{action}
		</div>
	);
}

function ConfirmFilledModal({ jobTitle, onConfirm, onCancel }) {
	return (
		<div className="modal-backdrop" onClick={onCancel}>
			<div className="modal-content max-w-sm" onClick={(event) => event.stopPropagation()}>
				<div className="text-center">
					<div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(142,71%,45%)]/10">
						<CheckCircle2 className="h-6 w-6 text-[hsl(142,71%,45%)]" />
					</div>
					<h2 className="mb-2 text-lg font-bold text-foreground">Mark Role as Filled?</h2>
					<p className="mb-4 text-sm text-muted-foreground">
						You&apos;re about to mark <strong className="text-foreground">{jobTitle}</strong> as filled.
						This will move it to your closed roles section.
					</p>
					<div className="flex gap-2">
						<button
							type="button"
							onClick={onCancel}
							className="flex flex-1 items-center justify-center rounded-md border border-border py-2 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={onConfirm}
							className="flex flex-1 items-center justify-center rounded-md bg-[hsl(142,71%,45%)] py-2 text-sm font-medium text-white transition-colors hover:bg-[hsl(142,76%,36%)]"
						>
							Yes, Mark as Filled
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default function JobsTab() {
	const { companyName, branding, jobs, plan, jobsQuery, goToUpgrade } = useCompanyDashboard();

	const [searchQuery, setSearchQuery] = React.useState("");
	const [typeFilter, setTypeFilter] = React.useState("all");
	const [levelFilter, setLevelFilter] = React.useState("all");
	const [activeRoleTab, setActiveRoleTab] = React.useState("open");
	const [activeMenu, setActiveMenu] = React.useState(null);
	const [showJobModal, setShowJobModal] = React.useState(false);
	const [jobModalMode, setJobModalMode] = React.useState("create");
	const [selectedJob, setSelectedJob] = React.useState(null);
	const [jobToConfirm, setJobToConfirm] = React.useState(null);
	const [jobToExtend, setJobToExtend] = React.useState(null);

	const filteredJobs = React.useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		return jobs.filter((job) => {
			const matchesSearch =
				!query ||
				job.title.toLowerCase().includes(query) ||
				job.location.toLowerCase().includes(query) ||
				job.company?.toLowerCase().includes(query);
			const matchesType = typeFilter === "all" || job.type === typeFilter;
			const matchesLevel = levelFilter === "all" || job.level === levelFilter;
			return matchesSearch && matchesType && matchesLevel;
		});
	}, [jobs, levelFilter, searchQuery, typeFilter]);

	const openJobs = filteredJobs.filter((job) => job.status === "open");
	const closedJobs = filteredJobs.filter((job) => job.status === "filled");
	const hasActiveFilters = Boolean(searchQuery || typeFilter !== "all" || levelFilter !== "all");
	const totalOpenJobs = jobs.filter((job) => job.status === "open").length;
	const totalClosedJobs = jobs.filter((job) => job.status === "filled").length;

	return (
		<ContentSection companyHeader>
			<div className="mx-auto max-w-4xl space-y-4">
				{plan === "starter" ? (
					<div className="relative overflow-hidden rounded-lg bg-primary-50 p-4 text-foreground">
						<div className="absolute right-0 top-0 h-40 w-40 -translate-y-1/2 translate-x-1/2 rounded-full bg-primary-100" />
						<div className="absolute bottom-0 left-0 h-32 w-32 translate-y-1/2 -translate-x-1/2 rounded-full bg-primary-100" />
						<div className="relative flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
							<div>
								<div className="mb-1 flex items-center gap-2">
									<Crown className="h-5 w-5 text-amber-400" />
									<h3 className="text-lg font-bold text-foreground">Unlock More Features</h3>
								</div>
								<p className="max-w-md text-sm text-muted-foreground">
									Upgrade to Corporate to get custom subdomains, embed widgets, advanced analytics, and more.
								</p>
							</div>
							<Button
								variant="secondary"
								className="shrink-0 border border-primary-200 bg-white text-primary-700 hover:bg-primary-100"
								onClick={goToUpgrade}
							>
								<Sparkles className="h-4 w-4" />
								Upgrade to Corporate
							</Button>
						</div>
					</div>
				) : null}

				<div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
					<div>
						<h1 className="text-xl font-bold text-foreground">Your Jobs</h1>
						<p className="text-sm text-muted-foreground">
							Manage and track all your job listings for {companyName}
						</p>
						{jobsQuery.isFetching && !jobsQuery.isLoading ? (
							<p className="mt-1 text-xs text-primary-700">Refreshing jobs from the backend...</p>
						) : null}
					</div>
					<Button
						onClick={() => {
							setJobModalMode("create");
							setSelectedJob(null);
							setShowJobModal(true);
						}}
					>
						<Plus className="mr-2 h-4 w-4" />
						Add New Job
					</Button>
				</div>

				<div className="flex w-fit gap-0.5 rounded-md bg-secondary p-0.5">
					<button
						type="button"
						onClick={() => setActiveRoleTab("open")}
						className={`rounded px-3 py-1.5 text-sm font-medium transition-all ${
							activeRoleTab === "open" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
						}`}
					>
						Open Roles
						<span className={`ml-1.5 rounded px-1.5 py-0.5 text-xs ${
							activeRoleTab === "open" ? "bg-blue-100 text-primary-700" : "bg-secondary text-muted-foreground"
						}`}>
							{totalOpenJobs}
						</span>
					</button>
					<button
						type="button"
						onClick={() => setActiveRoleTab("closed")}
						className={`rounded px-3 py-1.5 text-sm font-medium transition-all ${
							activeRoleTab === "closed" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
						}`}
					>
						Closed Roles
						<span className={`ml-1.5 rounded px-1.5 py-0.5 text-xs ${
							activeRoleTab === "closed" ? "bg-secondary text-foreground" : "bg-secondary text-muted-foreground"
						}`}>
							{totalClosedJobs}
						</span>
					</button>
				</div>

				<FilterBar
					searchValue={searchQuery}
					onSearchChange={setSearchQuery}
					searchPlaceholder="Search by title, company, or location..."
					dropdowns={[
						{
							value: typeFilter,
							onChange: setTypeFilter,
							options: [{ value: "all", label: "All Types" }, ...Array.from(new Set(jobs.map((job) => job.type))).map((item) => ({ value: item, label: item }))],
						},
						{
							value: levelFilter,
							onChange: setLevelFilter,
							options: [{ value: "all", label: "All Levels" }, ...Array.from(new Set(jobs.map((job) => job.level))).map((item) => ({ value: item, label: item }))],
						},
					]}
					clearButton={hasActiveFilters ? { show: true, onClick: () => {
						setSearchQuery("");
						setTypeFilter("all");
						setLevelFilter("all");
					} } : undefined}
				/>

				{jobsQuery.isLoading ? (
					<JobsLoadingState />
				) : jobsQuery.isError ? (
					<JobsErrorState onRetry={() => jobsQuery.refetch()} />
				) : activeRoleTab === "open" ? (
					<section>
						{openJobs.length === 0 ? (
							<JobsEmptyState
								icon={<Briefcase className="h-6 w-6 text-muted-foreground" />}
								title="No open roles"
								description={hasActiveFilters ? "No jobs match your current filters" : "No open jobs are currently available from the backend"}
								action={!hasActiveFilters ? (
									<button
										type="button"
										onClick={() => {
											setJobModalMode("create");
											setSelectedJob(null);
											setShowJobModal(true);
										}}
										className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors hover:bg-blue-100"
									>
										<Plus className="h-4 w-4" />
										Add New Job
									</button>
								) : undefined}
							/>
						) : (
							<div>
								{openJobs.map((job) => (
									<div key={job.id} className="border-b border-border bg-white py-4 transition-all hover:bg-gray-50">
										<div className="flex items-center gap-4">
											<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-blue-50">
												{branding.companyLogo || job.logoUrl ? (
													<img src={branding.companyLogo || job.logoUrl} alt={`${companyName} logo`} className="h-full w-full object-cover" />
												) : (
													<Briefcase className="h-5 w-5 text-primary-700" />
												)}
											</div>

											<div className="min-w-0 flex-1">
												<div className="mb-1 flex items-center gap-2">
													<span className="truncate font-semibold text-foreground">{job.title}</span>
												</div>
												<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
													<span className="flex items-center gap-1">
														<MapPin className="h-3.5 w-3.5" />
														{job.location}
													</span>
													<span className="flex items-center gap-1">
														<Calendar className="h-3.5 w-3.5" />
														{job.deadline ? dayjs(job.deadline).format("MMM D, YYYY") : "No deadline"}
													</span>
													<span className="flex items-center gap-1">
														<CircleDollarSign className="h-3.5 w-3.5" />
														{job.salary || "Negotiable"}
													</span>
												</div>
											</div>

											<div className="relative flex-shrink-0">
												<button
													type="button"
													onClick={() => setActiveMenu((current) => current === job.id ? null : job.id)}
													className="rounded p-1.5 transition-colors hover:bg-secondary"
												>
													<MoreVertical className="h-4 w-4 text-muted-foreground" />
												</button>

												{activeMenu === job.id ? (
													<>
														<div className="fixed inset-0 z-10" onClick={() => setActiveMenu(null)} />
														<div className="absolute right-0 top-full z-20 mt-1 w-48 rounded-lg border border-border bg-white py-1 shadow-lg">
															<button
																type="button"
																onClick={() => {
																	setSelectedJob(job);
																	setJobModalMode("edit");
																	setShowJobModal(true);
																	setActiveMenu(null);
																}}
																className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary"
															>
																<Pencil className="h-3.5 w-3.5" />
																Edit Job
															</button>
															<button
																type="button"
																onClick={() => {
																	setJobToExtend(job);
																	setActiveMenu(null);
																}}
																className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary"
															>
																<Calendar className="h-3.5 w-3.5" />
																Extend Deadline
															</button>
															<button
																type="button"
																onClick={() => {
																	setJobToConfirm(job);
																	setActiveMenu(null);
																}}
																className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground transition-colors hover:bg-secondary"
															>
																<CheckCircle2 className="h-3.5 w-3.5" />
																Mark as Filled
															</button>
															{plan !== "starter" ? (
																<button
																	type="button"
																	onClick={async () => {
																		await jobsQuery.refetch();
																		toast.success("Jobs refreshed from the backend.");
																		setActiveMenu(null);
																	}}
																	className="flex w-full items-center gap-2 px-3 py-2 text-sm text-primary-700 transition-colors hover:bg-blue-50"
																>
																	<RefreshCw className="h-3.5 w-3.5" />
																	<span>Refresh Freshness</span>
																	<Sparkles className="ml-auto h-3 w-3" />
																</button>
															) : null}
															<div className="my-1 border-t border-border" />
															<button
																type="button"
																onClick={() => {
																	toast.info(READ_ONLY_MESSAGE);
																	setActiveMenu(null);
																}}
																className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
															>
																<Trash2 className="h-3.5 w-3.5" />
																Delete Job
															</button>
														</div>
													</>
												) : null}
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</section>
				) : (
					<section>
						{closedJobs.length === 0 ? (
							<JobsEmptyState
								icon={<CheckCircle2 className="h-6 w-6 text-muted-foreground" />}
								title="No closed roles"
								description={hasActiveFilters ? "No closed jobs match your current filters" : "Roles past deadline or marked as filled will appear here"}
							/>
						) : (
							<div>
								{closedJobs.map((job) => (
									<div key={job.id} className="border-b border-border bg-white/60 py-4">
										<div className="flex items-center gap-4">
											<div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-secondary opacity-60">
												{branding.companyLogo || job.logoUrl ? (
													<img src={branding.companyLogo || job.logoUrl} alt={`${companyName} logo`} className="h-full w-full object-cover grayscale" />
												) : (
													<Briefcase className="h-5 w-5 text-muted-foreground" />
												)}
											</div>

											<div className="min-w-0 flex-1">
												<div className="mb-1 flex items-center gap-2">
													<span className="truncate font-semibold text-muted-foreground">{job.title}</span>
													<span className="flex flex-shrink-0 items-center gap-0.5 rounded-md bg-[#dff7ea] px-2 py-1 text-xs font-medium text-[#017336]">
														<CheckCircle2 className="h-3 w-3" />
														Filled
													</span>
												</div>
												<div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
													<span className="flex items-center gap-1">
														<MapPin className="h-3.5 w-3.5" />
														{job.location}
													</span>
													<span className="flex items-center gap-1">
														<CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
														Closed {job.closedDate || "recently"}
													</span>
													<span className="flex items-center gap-1">
														<CircleDollarSign className="h-3.5 w-3.5" />
														{job.salary || "Negotiable"}
													</span>
												</div>
											</div>
										</div>
									</div>
								))}
							</div>
						)}
					</section>
				)}
			</div>

			{showJobModal ? (
				<JobFormModal mode={jobModalMode} job={selectedJob} onClose={() => setShowJobModal(false)} />
			) : null}

			{jobToConfirm ? (
				<ConfirmFilledModal
					jobTitle={jobToConfirm.title}
					onCancel={() => setJobToConfirm(null)}
					onConfirm={() => {
						toast.info(READ_ONLY_MESSAGE);
						setJobToConfirm(null);
					}}
				/>
			) : null}

			{jobToExtend ? (
				<ExtendDeadlineModal job={jobToExtend} onClose={() => setJobToExtend(null)} />
			) : null}
		</ContentSection>
	);
}

function ExtendDeadlineModal({ job, onClose }) {
	const [newDeadline, setNewDeadline] = React.useState(job.deadline || dayjs().format("YYYY-MM-DD"));

	return (
		<Modal title="Extend Deadline" onClose={onClose} width="max-w-sm">
			<form
				onSubmit={(event) => {
					event.preventDefault();
					void newDeadline;
					toast.info(READ_ONLY_MESSAGE);
					onClose();
				}}
			>
				<p className="mb-3 text-sm text-muted-foreground">
					Set a new deadline for <span className="font-semibold text-foreground">{job.title}</span>.
				</p>
				<div className="mb-4">
					<label className="mb-1.5 block text-sm font-medium text-foreground">New Deadline</label>
					<input
						type="date"
						min={dayjs().format("YYYY-MM-DD")}
						value={newDeadline}
						onChange={(event) => setNewDeadline(event.target.value)}
						className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
					/>
					<p className="mt-1.5 text-xs text-muted-foreground">
						Current deadline: {job.deadline ? dayjs(job.deadline).format("MMM D, YYYY") : "Not set"}
					</p>
				</div>
				<div className="mt-5 flex gap-3">
					<Button type="button" variant="outline" className="flex-1" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" className="flex-1">
						Update Deadline
					</Button>
				</div>
			</form>
		</Modal>
	);
}

function JobFormModal({ mode, job, onClose }) {
	const [formData, setFormData] = React.useState(() => ({
		title: job?.title || "",
		region: job?.location?.split(", ")[1] || "",
		city: job?.location?.split(", ")[0] || "",
		streetAddress: "",
		type: job?.type || "Full-time",
		level: job?.level || "Mid-Level",
		workEnvironment: "In-office",
		description: job?.description || "",
		responsibilities: job?.responsibilities || "",
		qualifications: job?.qualifications || "",
		applicationProcess: job?.applicationProcess || "",
		deadline: job?.deadline || dayjs().format("YYYY-MM-DD"),
	}));

	const isEdit = mode === "edit";

	const updateField = (field, value) => {
		setFormData((current) => ({ ...current, [field]: value }));
	};

	return (
		<Modal title={isEdit ? "Edit Job" : "Add New Job"} onClose={onClose}>
			<p className="mb-4 text-xs text-muted-foreground">This form is integrated into Dproz, but save actions are currently read-only.</p>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					void formData;
					toast.info(READ_ONLY_MESSAGE);
					onClose();
				}}
				className="space-y-4"
			>
				<div className="grid gap-3 md:grid-cols-2">
					<div className="md:col-span-2">
						<label className="mb-1 block text-sm font-medium text-foreground">Job Title</label>
						<input value={formData.title} onChange={(event) => updateField("title", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
					</div>
					<div>
						<label className="mb-1 block text-sm font-medium text-foreground">Region</label>
						<input value={formData.region} onChange={(event) => updateField("region", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
					</div>
					<div>
						<label className="mb-1 block text-sm font-medium text-foreground">City</label>
						<input value={formData.city} onChange={(event) => updateField("city", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
					</div>
					<div>
						<label className="mb-1 block text-sm font-medium text-foreground">Type</label>
						<select value={formData.type} onChange={(event) => updateField("type", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
							{JOB_TYPE_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
						</select>
					</div>
					<div>
						<label className="mb-1 block text-sm font-medium text-foreground">Level</label>
						<select value={formData.level} onChange={(event) => updateField("level", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
							{JOB_LEVEL_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
						</select>
					</div>
					<div>
						<label className="mb-1 block text-sm font-medium text-foreground">Work Environment</label>
						<select value={formData.workEnvironment} onChange={(event) => updateField("workEnvironment", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
							{WORK_ENVIRONMENT_OPTIONS.map((item) => <option key={item} value={item}>{item}</option>)}
						</select>
					</div>
					<div>
						<label className="mb-1 block text-sm font-medium text-foreground">Application Deadline</label>
						<input type="date" min={dayjs().format("YYYY-MM-DD")} value={formData.deadline} onChange={(event) => updateField("deadline", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
					</div>
				</div>

				<div>
					<label className="mb-1 block text-sm font-medium text-foreground">Description</label>
					<textarea rows={3} value={formData.description} onChange={(event) => updateField("description", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
				</div>
				<div>
					<label className="mb-1 block text-sm font-medium text-foreground">Responsibilities</label>
					<textarea rows={4} value={formData.responsibilities} onChange={(event) => updateField("responsibilities", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
				</div>
				<div>
					<label className="mb-1 block text-sm font-medium text-foreground">Qualifications</label>
					<textarea rows={4} value={formData.qualifications} onChange={(event) => updateField("qualifications", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
				</div>
				<div>
					<label className="mb-1 block text-sm font-medium text-foreground">Application Process</label>
					<textarea rows={3} value={formData.applicationProcess} onChange={(event) => updateField("applicationProcess", event.target.value)} className="w-full rounded-lg border border-input px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
				</div>

				<div className="flex gap-3">
					<Button type="button" variant="outline" className="flex-1" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" className="flex-1">
						{isEdit ? "Save Changes" : "Create Job"}
					</Button>
				</div>
			</form>
		</Modal>
	);
}
